import { createContext, createElement, useContext, type ReactNode } from "react";
import { isNative } from "@cntrl/native";
import { BREAKPOINTS } from "@cntrl/tokens";
import { useMinWidth } from "@cntrl/ui";

/**
 * PHONE OR DESKTOP — the one place the app decides, and the one way a screen asks.
 *
 * The rule: the iOS app is always the phone layout, whatever its width (an iPad, a rotated large phone), and the
 * web is the phone layout below `lg`. The shells DECIDE it ({@link useShellPhoneDecision}) and PUBLISH the answer
 * ({@link PhoneLayoutProvider}); every screen, route wrapper and overlay ASKS it ({@link usePhoneLayout}).
 *
 * Before this, the shells decided and then ~50 screens re-derived the answer from the width alone, so the two
 * disagreed: the iOS app on a wide screen drew desktop screens inside the phone tab bar, and three roster
 * screens (which asked "is the band's mobile shell mounted?") drew desktop pop-ups on a phone.
 * `phoneLayout.guard.test.ts` keeps `useMinWidth("lg")` out of everything but this file.
 *
 * `useMinWidth("md")` is a different question — how much room a layout has — and stays where it is.
 */
const PhoneLayoutContext = createContext<boolean | null>(null);

/** The shell's published answer. Mounted by the authed shell layouts around everything they render. */
export function PhoneLayoutProvider({ phone, children }: { phone: boolean; children: ReactNode }) {
  return createElement(PhoneLayoutContext.Provider, { value: phone }, children);
}

/** THE decision. Only a shell layout calls this; everything else reads {@link usePhoneLayout}. */
export function useShellPhoneDecision(): boolean {
  const wide = useMinWidth("lg");
  return isNative() || !wide;
}

/**
 * Whether the screen is in its PHONE layout. Reads what the mounted shell chose; with no shell mounted (a screen
 * rendered on its own, the signed-out pages, a unit test) it falls back to the same rule.
 */
export function usePhoneLayout(): boolean {
  const chosen = useContext(PhoneLayoutContext);
  const decided = useShellPhoneDecision();
  return chosen ?? decided;
}

/** The same rule read once, outside React (a push tap handled before any screen renders). */
export function isPhoneLayoutNow(): boolean {
  if (isNative()) return true;
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && !window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`).matches;
}
